import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import * as TourOffersService from '~/services/TourOffersService';

import {
  TourOffer,
  TourOfferBookingTerms,
  TourOfferConfigurationContent,
  TourOfferContent,
  TourOfferSummary,
} from '~/types/tours/tourOffer';

export const tourOfferQueryKeys = {
  all: ['tour-offers'] as const,
  lists: () => [...tourOfferQueryKeys.all, 'list'] as const,
  list: (search: string, ltleOnly: boolean) => [...tourOfferQueryKeys.lists(), search, ltleOnly] as const,
  details: () => [...tourOfferQueryKeys.all, 'detail'] as const,
  detail: (tourOfferId: string | undefined) => [...tourOfferQueryKeys.details(), tourOfferId ?? ''] as const,
  departures: (tourOfferId: string | undefined) =>
    [...tourOfferQueryKeys.all, 'departures', tourOfferId ?? ''] as const,
  offer: (tourOfferId: string | undefined) => [...tourOfferQueryKeys.all, 'offer', tourOfferId ?? ''] as const,
};

/** Callers debounce, so each settled term is its own cache entry. */
export function useSearchTourOffers(search: string, ltleOnly: boolean) {
  return useQuery({
    queryKey: tourOfferQueryKeys.list(search, ltleOnly),
    queryFn: ({ signal }) => TourOffersService.searchTourOffers({ search: search || undefined, ltleOnly }, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** There is no single-offer summary endpoint; searching by id matches exactly. */
const tourOfferSummaryQuery = (tourOfferId: string | undefined) => ({
  queryKey: tourOfferQueryKeys.detail(tourOfferId),
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const offers = await TourOffersService.searchTourOffers({ search: tourOfferId }, { signal });
    return offers.find((offer) => offer.id === tourOfferId) ?? null;
  },
  enabled: !!tourOfferId,
});

export function useGetTourOfferSummary(tourOfferId: string | undefined) {
  return useQuery(tourOfferSummaryQuery(tourOfferId));
}

type TourOfferSummaryResult = { data?: TourOfferSummary | null };

// Module level so React Query keeps the combined Map stable between renders.
const summariesById = (results: Array<TourOfferSummaryResult>) =>
  new Map(results.flatMap((result) => (result.data ? [[result.data.id, result.data] as const] : [])));

/** By tour offer id, holding those that have loaded. */
export function useGetTourOfferSummaries(tourOfferIds: Array<string>) {
  return useQueries({
    queries: tourOfferIds.map(tourOfferSummaryQuery),
    combine: summariesById,
  });
}

export function useGetTourOfferDepartures(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.departures(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOfferDepartures(tourOfferId, { signal }),
    enabled: !!tourOfferId,
  });
}

/** The whole tour offer, with its content, booking terms and configurations. */
export function useGetTourOffer(tourOfferId: string | undefined) {
  return useQuery({
    queryKey: tourOfferQueryKeys.offer(tourOfferId),
    queryFn: ({ signal }) => TourOffersService.getTourOffer(tourOfferId, { signal }),
    enabled: !!tourOfferId,
  });
}

interface TourOfferChanges {
  name?: string;
  content?: TourOfferContent;
  bookingTerms?: TourOfferBookingTerms;
  /** By configuration id. */
  configurations?: Record<string, TourOfferConfigurationContent>;
}

/** Saves only the parts that changed; svc-tour takes each through its own endpoint. */
export function useSaveTourOffer(tourOfferId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      name,
      content,
      bookingTerms,
      configurations = {},
    }: TourOfferChanges): Promise<TourOffer | undefined> => {
      let offer: TourOffer | undefined;
      if (name !== undefined) offer = await TourOffersService.renameTourOffer(tourOfferId, name);
      if (content) offer = await TourOffersService.putTourOfferContent(tourOfferId, content);
      if (bookingTerms) offer = await TourOffersService.putTourOfferBookingTerms(tourOfferId, bookingTerms);
      for (const [configurationId, configuration] of Object.entries(configurations)) {
        offer = await TourOffersService.putTourOfferConfigurationContent(tourOfferId, configurationId, configuration);
      }
      return offer;
    },
    onSuccess: (offer) => {
      if (offer) queryClient.setQueryData(tourOfferQueryKeys.offer(tourOfferId), offer);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: tourOfferQueryKeys.detail(tourOfferId) });
      queryClient.invalidateQueries({ queryKey: tourOfferQueryKeys.offer(tourOfferId) });
    },
  });
}
